<template>
  <DxPopup
    :visible.sync="visible"
    :show-title="true"
    :title="popupTitle"
    :drag-enabled="false"
    :hide-on-outside-click="true"
    :show-close-button="false"
    width="90%"
    height="90%"
    :wrapper-attr="{ class: 'plan-print-popup' }"
  >
    <DxToolbarItem
      location="after"
      toolbar="top"
      widget="dxButton"
      :options="printButtonOptions"
    />
    <DxToolbarItem
      location="after"
      toolbar="top"
      widget="dxButton"
      :options="closeButtonOptions"
    />

    <DxScrollView direction="both">
      <div class="plan-print-content" ref="printContent">
        <div class="plan-print-header">
          <div>
            <div class="plan-print-title">Production Schedule</div>
            <div class="plan-print-meta">
              Branch: {{ branchLabel }} &middot; Period:
              {{ rangeLabel }} &middot; View: {{ viewModeLabel }}
            </div>
          </div>
          <div class="plan-print-timestamp">Printed: {{ printedAtLabel }}</div>
        </div>

        <table class="plan-print-table">
          <thead>
            <tr>
              <th class="plan-print-th-corner">Process</th>
              <th
                v-for="col in columns"
                :key="col.key"
                class="plan-print-th-day"
              >
                {{ col.label
                }}<span class="plan-print-th-day-sub">{{ col.subLabel }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="proc in processes" :key="proc.ProcessCode">
              <td class="plan-print-td-proc">
                <div class="plan-print-row-code">{{ proc.Description }}</div>
                <div class="plan-print-row-name">{{ proc.ProcessCode }}</div>
              </td>
              <td
                v-for="col in columns"
                :key="proc.ProcessCode + '-' + col.key"
                class="plan-print-td-cell"
              >
                <div class="plan-print-td-cell-inner">
                  <template
                    v-if="getCellTiles(proc.ProcessCode, col.key).length"
                  >
                    <div
                      v-for="tile in getCellTiles(proc.ProcessCode, col.key)"
                      :key="tile.RecordId"
                      class="plan-print-tile"
                      :style="tileStyle(tile)"
                    >
                      <template v-if="tile.WOType === 'Maintenance'">
                        <div class="plan-print-tile-maint-code">
                          {{ tile.WorkOrderCode }}
                        </div>
                        <div class="plan-print-tile-maint-name">
                          {{ tile.ProductDescription1 }}
                        </div>
                      </template>
                      <template v-else>
                        <div class="plan-print-tile-prod">
                          <span class="plan-print-tile-prod-text"
                            >{{ tile.ProductCode }} -
                            {{ tile.ProductDescription1 }}</span
                          >
                          <span
                            v-if="tile.SalesOrderCode === 0"
                            class="plan-print-mts-badge"
                            >MTS</span
                          >
                        </div>
                        <div class="plan-print-tile-wo">
                          {{ woCodeDisplay(tile) }}
                        </div>
                        <div
                          v-if="tile.SalesOrderCode !== 0"
                          class="plan-print-tile-customer"
                        >
                          {{ tile.CustomerName }}
                        </div>
                        <div class="plan-print-tile-qty">
                          {{ quantityTonnes(tile) }} t<span
                            v-if="tile.SalesOrderCode !== 0"
                            >&middot; SO-{{ tile.SalesOrderCode }}</span
                          >
                        </div>
                      </template>
                    </div>
                  </template>
                  <span v-else class="plan-print-td-cell-empty">—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DxScrollView>
  </DxPopup>
</template>

<script>
import { formatNumber } from "devextreme/localization";
import { sequenceToAlpha } from "@/_helpers/formats/format";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const VIEW_MODE_LABELS = {
  day: "Day",
  workweek: "Work Week (Mon-Fri)",
  week: "Week (Mon-Sat)",
};

const PRINT_ROOT_ID = "plan-print-root";

function hexToRgb(hex) {
  const clean = (hex || "").replace("#", "");
  const bigint = parseInt(clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

// Mixes a hex colour with white; ratio 0 = white, 1 = full colour — same helper as plan-tile.vue
function tint(hex, ratio) {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel) => Math.round(channel * ratio + 255 * (1 - ratio));
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function formatDate(dt) {
  return `${String(dt.getDate()).padStart(2, "0")}-${
    MONTH_NAMES[dt.getMonth()]
  }-${dt.getFullYear()}`;
}

function formatDateTime(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

export default {
  name: "PlanPrintView",

  props: {
    branchLabel: { type: String, default: "" },
    viewMode: { type: String, required: true },
    columns: { type: Array, required: true },
    processes: { type: Array, required: true },
    getCellTiles: { type: Function, required: true },
    // Count of SO lines linked to each Work Order, keyed by WorkOrderCode
    // (Task 73070) — same map production-schedule-plan.vue passes to
    // plan-tile.vue, reused here so print matches the on-screen suffix rule.
    workOrderLineCounts: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      visible: false,
      printedAt: null,
    };
  },

  computed: {
    popupTitle() {
      return `Production Schedule — ${this.rangeLabel}`;
    },

    rangeLabel() {
      if (!this.columns.length) return "";
      const first = this.columns[0].dateObj;
      const last = this.columns[this.columns.length - 1].dateObj;
      return this.columns.length === 1
        ? formatDate(first)
        : `${formatDate(first)} – ${formatDate(last)}`;
    },

    viewModeLabel() {
      return VIEW_MODE_LABELS[this.viewMode] || this.viewMode;
    },

    printedAtLabel() {
      return this.printedAt ? formatDateTime(this.printedAt) : "";
    },

    printButtonOptions() {
      return {
        text: "Print / Save PDF",
        icon: "print",
        type: "default",
        stylingMode: "contained",
        onClick: this.doPrint,
      };
    },

    closeButtonOptions() {
      return {
        text: "Close",
        stylingMode: "outlined",
        onClick: this.close,
      };
    },
  },

  methods: {
    // Called by production-schedule-plan.vue when the toolbar's Print button is clicked —
    // opens the preview popup (matches prototype.html's openPrint(), which shows a preview
    // before the user actually triggers the browser print dialog).
    open() {
      this.printedAt = new Date();
      this.visible = true;
    },

    close() {
      this.visible = false;
    },

    // Derived from Position (not WorkOrderSequence), and only shown once
    // the WO has 2+ linked SO lines within the same process — mirrors
    // plan-tile.vue's alphaSuffix. Keyed by WorkOrderCode+ProcessCode, same
    // as production-schedule-plan.vue's workOrderLineCounts computed.
    alphaSuffix(tile) {
      const key = `${tile.WorkOrderCode}|${tile.ProcessCode}`;
      const count = this.workOrderLineCounts[key] || 1;
      return count >= 2 ? sequenceToAlpha(tile.Position) : "";
    },

    woCodeDisplay(tile) {
      const suffix = this.alphaSuffix(tile);
      return suffix ? `${tile.WorkOrderCode} ${suffix}` : tile.WorkOrderCode;
    },

    quantityTonnes(tile) {
      // SalesOrderStockUMQuantity assumed to always be in KG (documented limitation, same as plan-tile.vue)
      return formatNumber(
        (tile.SalesOrderStockUMQuantity || 0) / 1000,
        "##,###,###,###,##0.00",
      );
    },

    // Same status/type -> colour mapping as plan-tile.vue (which mirrors the
    // Timeline view's tile palette) + the same tint helper — print uses a
    // lighter background ratio and a solid left bar instead of a top bar.
    accentColor(tile) {
      if (tile.WOType === "Maintenance") return "#808080";

      const status = tile.FriendlyStatus
        ? tile.FriendlyStatus.toFriendlyStatus()
        : false;

      switch (status) {
        case "Awaiting Production":
          return "#62d26f";
        case "In Production":
          return "#ffa500";
        case "Completed":
          return "#4186e0";
        case "Cancelled":
          return "#e8384f";
        case "Draft":
          return "#eec300";
        case "Planning":
          return "#8b5cf6";
        case "Review":
          return "#ff6f61";
        default:
          return "#9ca3af";
      }
    },

    tileStyle(tile) {
      const accentColor = this.accentColor(tile);
      return {
        background: tint(accentColor, 0.12),
        borderLeftColor: accentColor,
      };
    },

    // Clones the already-rendered preview markup into a plain node appended directly to
    // <body> (outside the Vue-managed tree) and hides every other body child via CSS —
    // mirrors prototype.html's doPrint() pattern (DF-6: dedicated print CSS).
    doPrint() {
      const existing = document.getElementById(PRINT_ROOT_ID);
      if (existing) existing.remove();

      const root = document.createElement("div");
      root.id = PRINT_ROOT_ID;
      root.innerHTML = this.$refs.printContent.innerHTML;
      document.body.appendChild(root);

      window.addEventListener("afterprint", () => root.remove(), {
        once: true,
      });

      window.print();
    },
  },
};
</script>

<style lang="scss">
.plan-print-popup {
  .dx-popup-content {
    overflow: auto;
    padding: 10px;

    background: #e1e1e9c7;

    > div.dx-scrollable {
      background: #fff;
      padding: 10px;
    }
  }

  .dx-toolbar-item {
    .dx-button-content .dx-icon::before {
      color: #fff !important;
    }
  }

  .dx-toolbar-items-container {
    height: 60px !important;
  }
}
</style>

<style lang="scss">
.plan-print-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.plan-print-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
}

.plan-print-meta {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
}

.plan-print-timestamp {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

.plan-print-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #d1d5db;
  margin-top: 14px;
}

.plan-print-table thead {
  display: table-header-group;
}

.plan-print-th-corner,
.plan-print-th-day {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-bottom: 2px solid #d1d5db;
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
}

.plan-print-th-corner {
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  vertical-align: bottom;
  min-width: 100px;
}

.plan-print-th-day {
  text-align: center;
  white-space: nowrap;
  min-width: 115px;
}

.plan-print-th-day-sub {
  display: block;
  font-weight: 400;
  color: #9ca3af;
  margin-top: 1px;
}

.plan-print-td-proc {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  padding: 5px 8px;
  vertical-align: top;
  min-width: 100px;
}

.plan-print-row-code {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
}

.plan-print-row-name {
  font-size: 10px;
  color: #9ca3af;
}

.plan-print-td-cell {
  border: 1px solid #e5e7eb;
  padding: 3px;
  vertical-align: top;
  min-width: 115px;
  break-inside: avoid;
}

.plan-print-td-cell-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.plan-print-td-cell-empty {
  font-size: 9px;
  color: #d1d5db;
}

.plan-print-tile {
  font-size: 10px;
  border-radius: 2px;
  padding: 3px 5px 4px;
  border-left: 3px solid transparent;
  break-inside: avoid;
}

.plan-print-tile-prod {
  font-weight: 600;
  color: #111827;
  display: flex;
  gap: 3px;
  align-items: flex-start;
}

.plan-print-tile-prod-text {
  flex: 1;
  white-space: normal;
  word-break: break-word;
}

.plan-print-mts-badge {
  font-size: 7px;
  font-weight: 700;
  color: #fff;
  background: #64748b;
  border-radius: 2px;
  padding: 0 3px;
  flex-shrink: 0;
}

.plan-print-tile-wo {
  color: #64748b;
  margin-top: 1px;
  font-variant-numeric: tabular-nums;
}

.plan-print-tile-customer {
  color: #374151;
  margin-top: 1px;
}

.plan-print-tile-qty {
  font-weight: 600;
  color: #111827;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.plan-print-tile-maint-code {
  font-weight: 600;
  color: #374151;
}

.plan-print-tile-maint-name {
  color: #4b5563;
  margin-top: 1px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
</style>

<style lang="scss">
/* Unscoped: doPrint() clones printContent's innerHTML into a plain node appended
   directly to <body>, outside this component's rendered (scoped) tree, so scoped
   data-v attributes wouldn't match it there. Gated under @media print so it never
   affects the on-screen preview popup (styled above, under scoped CSS instead). */
@media print {
  @page {
    size: A3 landscape;
    margin: 8mm;

    @bottom-right {
      content: "Page " counter(page) " of " counter(pages);
      font-size: 9pt;
      color: #9ca3af;
    }
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html,
  body {
    height: auto !important;
    overflow: visible !important;
  }

  body > *:not(#plan-print-root) {
    display: none !important;
  }

  #plan-print-root {
    display: block !important;
    position: static !important;
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    font-family: "Roboto", "Segoe UI", system-ui, sans-serif;
    color: #111827;
  }

  #plan-print-root .plan-print-table {
    margin-top: 8px;
  }

  #plan-print-root .plan-print-td-cell,
  #plan-print-root .plan-print-tile {
    break-inside: avoid;
  }
}
</style>
